import React, { useState, useEffect, useMemo } from 'react';
import { getCurrentUser } from '@/data/users';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileCheck, Clock, CheckCircle, XCircle, User, BookOpen, Calendar, 
  ChevronDown, ChevronUp, Eye, RefreshCw, ArrowLeft, FileText, Layers, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const SUBJECT_COLORS = {
  'ICT': 'from-blue-500 to-cyan-500',
  'ICT / Computer Studies': 'from-blue-500 to-cyan-500',
  'French': 'from-purple-500 to-pink-500',
  'English': 'from-emerald-500 to-teal-500',
  'Mathematics': 'from-orange-500 to-amber-500',
  'Science': 'from-green-500 to-lime-500',
  'History': 'from-yellow-600 to-amber-600',
  'Geography': 'from-teal-500 to-green-500',
  'Biology': 'from-green-600 to-emerald-600',
  'Chemistry': 'from-indigo-500 to-purple-500',
  'Physics': 'from-slate-500 to-zinc-500',
  'Economics': 'from-amber-500 to-yellow-500',
  'Accounting': 'from-rose-500 to-pink-500',
  'Agriculture': 'from-lime-600 to-green-600',
  'Development Studies': 'from-sky-500 to-blue-500',
};

const SUBJECT_ICONS = {
  'ICT': '💻', 'ICT / Computer Studies': '💻',
  'French': '🇫🇷', 'English': '📖', 'Mathematics': '📐',
  'Science': '🔬', 'History': '📜', 'Geography': '🌍',
  'Biology': '🧬', 'Chemistry': '⚗️', 'Physics': '⚡',
  'Economics': '📊', 'Accounting': '🧮', 'Agriculture': '🌱',
  'Development Studies': '🏛️',
};

export default function PendingApprovalsPage() {
  const currentUser = getCurrentUser();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [activeTab, setActiveTab] = useState('scheme');
  const [expandedId, setExpandedId] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const schoolId = currentUser?.school_id || currentUser?.schoolId || 
    (currentUser?.id?.includes('school-') ? currentUser.id.substring(currentUser.id.indexOf('school-')) : null);

  useEffect(() => {
    if (schoolId) fetchSubmissions();
    else setLoading(false);
  }, [schoolId]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/academic/all/${schoolId}`);
      const data = await response.json();
      if (response.ok) {
        setSubmissions(Array.isArray(data) ? data : []);
      } else {
        toast.error('Failed to load submissions');
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (submissionId, status) => {
    setReviewing(submissionId);
    try {
      const response = await fetch(`${API_URL}/api/academic/review/${submissionId}?reviewer_id=${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, comment: '' })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Submission ${status}!`);
        fetchSubmissions();
      } else {
        toast.error(data.detail || 'Failed to review submission');
      }
    } catch (error) {
      toast.error('Failed to review submission');
    } finally {
      setReviewing(null);
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/academic/submission/${submissionId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Submission deleted');
        fetchSubmissions();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  // Group submissions by subject
  const subjectGroups = useMemo(() => {
    const groups = {};
    submissions.forEach(sub => {
      const subj = sub.subject || 'Other';
      if (!groups[subj]) {
        groups[subj] = { schemes: [], lessons: [], teachers: new Set(), pending: 0 };
      }
      if (sub.submission_type === 'scheme') groups[subj].schemes.push(sub);
      else groups[subj].lessons.push(sub);
      if (sub.teacher_name) groups[subj].teachers.add(sub.teacher_name);
      if (sub.status === 'pending') groups[subj].pending++;
    });
    return groups;
  }, [submissions]);

  const totalPending = submissions.filter(s => s.status === 'pending').length;

  // Filtered submissions for current subject + tab
  const currentSubmissions = useMemo(() => {
    if (!selectedSubject) return [];
    const group = subjectGroups[selectedSubject];
    if (!group) return [];
    const list = activeTab === 'scheme' ? group.schemes : group.lessons;
    if (statusFilter === 'all') return list;
    return list.filter(s => s.status === statusFilter);
  }, [selectedSubject, activeTab, subjectGroups, statusFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="pending-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teachers Planning</h1>
          <p className="text-gray-500">Review and approve teacher Schemes of Work and Lesson Plans</p>
        </div>
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1">
              {totalPending} Pending
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchSubmissions} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-2 text-gray-500">Loading submissions...</p>
          </CardContent>
        </Card>
      ) : !selectedSubject ? (
        /* ========== SUBJECT CARDS VIEW ========== */
        <div data-testid="subject-cards-view">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Subjects</h2>
          {Object.keys(subjectGroups).length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-12 text-center">
                <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900">No submissions yet</h3>
                <p className="text-gray-500">Teacher planning submissions will appear here organized by subject.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Object.entries(subjectGroups).map(([subject, group]) => (
                <Card
                  key={subject}
                  className="border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => { setSelectedSubject(subject); setActiveTab('scheme'); setExpandedId(null); setStatusFilter('all'); }}
                  data-testid={`subject-card-${subject.toLowerCase().replace(/[\s\/]/g, '-')}`}
                >
                  <div className={`h-2 bg-gradient-to-r ${SUBJECT_COLORS[subject] || 'from-gray-400 to-gray-500'}`} />
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-3xl">{SUBJECT_ICONS[subject] || '📚'}</div>
                      {group.pending > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                          {group.pending} pending
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-bold text-gray-800 text-lg group-hover:text-emerald-600 transition-colors">
                      {subject}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Layers className="h-3 w-3" /> {group.schemes.length} Scheme{group.schemes.length !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" /> {group.lessons.length} Lesson{group.lessons.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      <User className="h-3 w-3 inline mr-1" />
                      {[...group.teachers].join(', ')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ========== SUBJECT DETAIL VIEW ========== */
        <div data-testid="subject-detail-view">
          {/* Back button + Subject header */}
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedSubject(null)}
              className="hover:bg-emerald-50"
              data-testid="back-to-subjects-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              All Subjects
            </Button>
            <div className={`h-6 w-1 rounded-full bg-gradient-to-b ${SUBJECT_COLORS[selectedSubject] || 'from-gray-400 to-gray-500'}`} />
            <h2 className="text-lg font-semibold text-gray-800">
              {SUBJECT_ICONS[selectedSubject] || '📚'} {selectedSubject}
              <span className="text-sm font-normal text-gray-500 ml-2">
                by {[...subjectGroups[selectedSubject]?.teachers || []].join(', ')}
              </span>
            </h2>
          </div>

          {/* Scheme / Lesson Plan tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setExpandedId(null); }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <TabsList>
                <TabsTrigger value="scheme" className="flex items-center gap-2" data-testid="tab-scheme">
                  <Layers className="h-4 w-4" />
                  Scheme of Work
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {subjectGroups[selectedSubject]?.schemes.length || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="lesson" className="flex items-center gap-2" data-testid="tab-lesson">
                  <FileText className="h-4 w-4" />
                  Lesson Plans
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {subjectGroups[selectedSubject]?.lessons.length || 0}
                  </Badge>
                </TabsTrigger>
              </TabsList>

              {/* Status filter */}
              <div className="flex gap-1.5">
                {['all', 'pending', 'approved', 'rejected'].map(s => (
                  <Button
                    key={s}
                    variant={statusFilter === s ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(s)}
                    className={`text-xs ${statusFilter === s ? (s === 'pending' ? 'bg-yellow-600' : s === 'approved' ? 'bg-green-600' : s === 'rejected' ? 'bg-red-600' : 'bg-blue-600') : ''}`}
                    data-testid={`filter-${s}`}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <TabsContent value={activeTab} className="mt-4">
              {currentSubmissions.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-10 text-center">
                    <FileCheck className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">
                      No {activeTab === 'scheme' ? 'schemes of work' : 'lesson plans'} 
                      {statusFilter !== 'all' ? ` with "${statusFilter}" status` : ''} for {selectedSubject}.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {currentSubmissions.map((sub) => (
                    <SubmissionCard
                      key={sub.id}
                      sub={sub}
                      expanded={expandedId === sub.id}
                      onToggle={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                      onReview={handleReview}
                      onDelete={handleDelete}
                      reviewing={reviewing}
                      getStatusBadge={getStatusBadge}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ sub, expanded, onToggle, onReview, onDelete, reviewing, getStatusBadge, formatDate }) {
  return (
    <Card className="overflow-hidden border-0 shadow-sm" data-testid={`submission-${sub.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {getStatusBadge(sub.status)}
              {sub.year_level && <Badge variant="outline" className="text-xs">Year {sub.year_level}</Badge>}
              {sub.term && <Badge variant="outline" className="text-xs">Term {sub.term}</Badge>}
            </div>
            <CardTitle className="text-base truncate">{sub.title}</CardTitle>
            <CardDescription className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" /> {sub.teacher_name}
              </span>
            </CardDescription>
          </div>
          <div className="text-right text-xs text-gray-500 flex-shrink-0 ml-4">
            <p>Submitted: {formatDate(sub.submitted_at)}</p>
            {sub.reviewed_at && <p>Reviewed: {formatDate(sub.reviewed_at)}</p>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" size="sm"
            onClick={onToggle}
            className="text-blue-600"
            data-testid={`toggle-details-${sub.id}`}
          >
            <Eye className="h-4 w-4 mr-1" />
            {expanded ? 'Hide Details' : 'View Details'}
            {expanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
          
          {sub.status === 'pending' && (
            <div className="flex gap-2">
              <Button 
                variant="outline" size="sm"
                className="text-red-600 hover:bg-red-50"
                onClick={() => onReview(sub.id, 'rejected')}
                disabled={reviewing === sub.id}
                data-testid={`reject-${sub.id}`}
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button 
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onReview(sub.id, 'approved')}
                disabled={reviewing === sub.id}
                data-testid={`approve-${sub.id}`}
              >
                <CheckCircle className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button 
                variant="ghost" size="sm"
                className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                onClick={() => onDelete(sub.id)}
                data-testid={`delete-${sub.id}`}
                title="Delete submission"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
          {sub.status !== 'pending' && (
            <Button 
              variant="ghost" size="sm"
              className="text-gray-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => onDelete(sub.id)}
              data-testid={`delete-${sub.id}`}
              title="Delete submission"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
        </div>

        {sub.reviewer_comment && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600"><strong>Comment:</strong> {sub.reviewer_comment}</p>
          </div>
        )}

        {expanded && <SubmissionDetails submissionId={sub.id} />}
      </CardContent>
    </Card>
  );
}

function SubmissionDetails({ submissionId }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/api/academic/submission/${submissionId}`);
        const data = await response.json();
        if (response.ok) setDetails(data);
      } catch (error) {
        console.error('Error fetching details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [submissionId]);

  if (loading) return <div className="mt-4 p-4 text-center text-gray-500"><RefreshCw className="h-5 w-5 animate-spin mx-auto" /></div>;
  if (!details?.content) return <div className="mt-4 p-4 text-center text-gray-500">No content available</div>;

  const content = typeof details.content === 'string' ? JSON.parse(details.content) : details.content;

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg max-h-[600px] overflow-y-auto">
      <h4 className="font-semibold mb-3 text-base">Full Content Details</h4>
      {details.submission_type === 'scheme' ? (
        <SchemePreview content={content} />
      ) : (
        <LessonPreview content={content} />
      )}
    </div>
  );
}

function SchemePreview({ content }) {
  if (Array.isArray(content)) {
    return (
      <div className="space-y-4">
        {content.map((week, idx) => (
          <div key={idx} className="p-4 bg-white rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-blue-100 text-blue-800 font-bold">Week {week.week || idx + 1}</Badge>
              <h5 className="font-semibold text-gray-900">{week.topic || week.title || 'No topic'}</h5>
            </div>

            {week.learning_objectives && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Learning Objectives</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 pl-1">
                  {(Array.isArray(week.learning_objectives) ? week.learning_objectives : [week.learning_objectives]).map((obj, i) => (
                    <li key={i}>{obj}</li>
                  ))}
                </ul>
              </div>
            )}

            {week.activities && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Activities</p>
                {Array.isArray(week.activities) ? (
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 pl-1">
                    {week.activities.map((act, i) => (
                      <li key={i}>{typeof act === 'string' ? act : act.name || act.description || JSON.stringify(act)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700">{week.activities}</p>
                )}
              </div>
            )}

            {week.resources && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Resources</p>
                <p className="text-sm text-gray-700">{Array.isArray(week.resources) ? week.resources.join(', ') : week.resources}</p>
              </div>
            )}

            {week.assessment && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Assessment</p>
                <p className="text-sm text-gray-700">{Array.isArray(week.assessment) ? week.assessment.join(', ') : week.assessment}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-xs overflow-auto whitespace-pre-wrap bg-white p-3 rounded border">{JSON.stringify(content, null, 2)}</pre>;
}

function LessonPreview({ content }) {
  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="flex flex-wrap gap-2">
        {content.subject && <Badge variant="outline">Subject: {content.subject}</Badge>}
        {content.topic && <Badge variant="outline">Topic: {content.topic}</Badge>}
        {content.strand && <Badge variant="outline">Strand: {content.strand}</Badge>}
        {content.year && <Badge variant="outline">Year {content.year}</Badge>}
        {content.term && <Badge variant="outline">Term {content.term}</Badge>}
        {content.week && <Badge variant="outline">Week {content.week}</Badge>}
      </div>

      {/* If it has days (weekly plan) */}
      {content.days && Array.isArray(content.days) ? (
        <div className="space-y-4">
          {content.days.map((day, idx) => (
            <div key={idx} className="p-4 bg-white rounded-lg border shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 font-bold">{day.day || `Day ${idx + 1}`}</Badge>
                  <h5 className="font-semibold text-gray-900">{day.title}</h5>
                </div>
                {day.duration && <Badge variant="outline" className="text-xs">{day.duration}</Badge>}
              </div>

              {day.introduction && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Introduction</p>
                  <p className="text-sm text-gray-700 bg-blue-50 p-2 rounded">{day.introduction}</p>
                </div>
              )}

              {day.objectives && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Objectives</p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 pl-1">
                    {(Array.isArray(day.objectives) ? day.objectives : [day.objectives]).map((obj, i) => (
                      <li key={i}>{obj}</li>
                    ))}
                  </ul>
                </div>
              )}

              {day.activities && Array.isArray(day.activities) && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Activities</p>
                  <div className="space-y-2">
                    {day.activities.map((act, i) => (
                      <div key={i} className="flex gap-3 text-sm bg-gray-50 p-2 rounded">
                        <div className="flex-shrink-0">
                          <Badge variant="outline" className="text-xs">{act.duration || ''}</Badge>
                        </div>
                        <div>
                          <span className="font-medium text-gray-800">{act.name}</span>
                          {act.description && <p className="text-gray-600 text-xs mt-0.5">{act.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {day.materials && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Materials</p>
                  <p className="text-sm text-gray-700">{day.materials}</p>
                </div>
              )}

              {day.assessment && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Assessment</p>
                  <p className="text-sm text-gray-700">{day.assessment}</p>
                </div>
              )}

              {day.homework && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Homework</p>
                  <p className="text-sm text-gray-700 bg-yellow-50 p-2 rounded">{day.homework}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Single lesson plan */
        <div className="p-4 bg-white rounded-lg border shadow-sm">
          {content.title && <h5 className="font-semibold text-gray-900 mb-2">{content.title}</h5>}

          {content.objectives && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Objectives</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 pl-1">
                {(Array.isArray(content.objectives) ? content.objectives : [content.objectives]).map((obj, i) => (
                  <li key={i}>{obj}</li>
                ))}
              </ul>
            </div>
          )}

          {content.introduction && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Introduction</p>
              <p className="text-sm text-gray-700 bg-blue-50 p-2 rounded">{content.introduction}</p>
            </div>
          )}

          {content.activities && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Activities</p>
              {Array.isArray(content.activities) ? (
                <div className="space-y-2">
                  {content.activities.map((act, i) => (
                    <div key={i} className="text-sm bg-gray-50 p-2 rounded">
                      {typeof act === 'string' ? act : (
                        <div className="flex gap-3">
                          {act.duration && <Badge variant="outline" className="text-xs flex-shrink-0">{act.duration}</Badge>}
                          <div>
                            <span className="font-medium">{act.name}</span>
                            {act.description && <p className="text-gray-600 text-xs mt-0.5">{act.description}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700">{content.activities}</p>
              )}
            </div>
          )}

          {content.materials && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Materials</p>
              <p className="text-sm text-gray-700">{content.materials}</p>
            </div>
          )}

          {content.assessment && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Assessment</p>
              <p className="text-sm text-gray-700">{content.assessment}</p>
            </div>
          )}

          {content.homework && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Homework</p>
              <p className="text-sm text-gray-700 bg-yellow-50 p-2 rounded">{content.homework}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
